import { useAtomValue } from "jotai";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { UseFormReturn } from "react-hook-form";

import {
  isArrayOfIntegersEqual,
  padTo16LengthUsingZeros,
  removeNonNumber,
} from "cc-tools/helpers";

import { cardNumberMetaDiuAtom } from "checkout-experience/atoms/payment-atoms";
import { usePaymentOptionContext } from "checkout-experience/components/payment-method/payment-option-provider";
import { useChexContext } from "checkout-experience/providers/chex-context-wrapper";
import { getIsCobrandedCardNetworksEnabled } from "checkout-experience/utils/validators/card-network";
import { useCardNetworksApiLookup } from "checkout-query-client/hooks/use-card-networks-api-lookup";
import { UseQueryResult } from "checkout-query-client/main";

import { CardNetwork } from "frontend-client";

import { getIsFeatureEnabled } from "splitio-loader/src/utils/get-split-io-flag";

export const useCardNetworksApiLookupChex = ({
  watch,
  getFieldState,
}: UseFormReturn):
  | {
      isCardNetworksApiLookupTooLong: boolean;
      possibleCardNetworksApi: Array<CardNetwork> | undefined;
      setPossibleCardNetworksApi: () => void;
    }
  | (UseQueryResult<unknown, Error> & {
      isCardNetworksApiLookupTooLong: boolean;
      possibleCardNetworksApi: Array<CardNetwork> | undefined;
      setPossibleCardNetworksApi: Dispatch<
        SetStateAction<Array<CardNetwork> | undefined>
      >;
    }) => {
  const [fieldValidAtLeastOnce, setFieldIsValidAtLeastOnce] = useState(false);
  const [isFieldLengthValidForLookup, setIsFieldLengthValidForLookup] =
    useState(false);

  const [isCardNetworksApiLookupTooLong, setIsCardNetworksApiLookupTooLong] =
    useState(false);
  const [possibleCardNetworksApi, setPossibleCardNetworksApi] = useState<
    Array<CardNetwork> | undefined
  >();

  // TODO: CB: prevent fast typers from skipping over api lookup
  const [hadSuccessQuery, setHadSuccessfulQuery] = useState(false);

  const { paymentOption } = usePaymentOptionContext() ?? {};
  const { formId = "", paymentType } = paymentOption ?? {};

  const {
    merchantSettings,
    experienceType,
    apiHost,
    checkoutSessionToken,
    target,
    version = "",
  } = useChexContext();

  const isCobrandedCardsEnabled =
    getIsFeatureEnabled(target, "co-branded-cards") &&
    getIsCobrandedCardNetworksEnabled(merchantSettings);

  const { invalid, isDirty, isTouched } = getFieldState(`${formId}.cardNumber`);

  const cardNumberValue = watch(`${formId}.cardNumber`) as string;
  const cardNumberMetaDiu = useAtomValue(cardNumberMetaDiuAtom);

  useEffect(() => {
    if (
      !invalid &&
      // Difference in behavior due to field frame implementation
      ((experienceType !== "dropInUI" && isTouched) ||
        (experienceType === "dropInUI" && isDirty)) &&
      !fieldValidAtLeastOnce
    ) {
      setFieldIsValidAtLeastOnce(true);
    }
  }, [invalid, isDirty, isTouched, experienceType]);

  const formattedCardNumberValue = useMemo(() => {
    if (experienceType === "dropInUI") {
      if (
        cardNumberMetaDiu?.cardNumberLength &&
        cardNumberMetaDiu?.cardNumberLength >= 8 &&
        !possibleCardNetworksApi &&
        !hadSuccessQuery
      ) {
        setIsFieldLengthValidForLookup(true);
      } else if (
        cardNumberMetaDiu?.cardNumberLength &&
        cardNumberMetaDiu?.cardNumberLength < 8
      ) {
        setHadSuccessfulQuery(false);
        setIsFieldLengthValidForLookup(false);
        setPossibleCardNetworksApi(undefined);
      } else {
        setIsFieldLengthValidForLookup(false);
      }
      return (
        cardNumberMetaDiu?.cardNumberEncryptedStrippedAndPadded
          ?.encryptedData || ""
      );
    } else {
      // experienceType !== "dropInUI"
      const strippedCardNumberValue = removeNonNumber(cardNumberValue);
      if (
        strippedCardNumberValue?.length >= 8 &&
        !possibleCardNetworksApi &&
        !hadSuccessQuery
      ) {
        setIsFieldLengthValidForLookup(true);
      } else if (strippedCardNumberValue?.length < 8) {
        // too short, clear existing so it can be queried
        setHadSuccessfulQuery(false);
        setIsFieldLengthValidForLookup(false);
        setPossibleCardNetworksApi(undefined);
      } else {
        setIsFieldLengthValidForLookup(false);
      }
      return padTo16LengthUsingZeros(strippedCardNumberValue);
    }
  }, [cardNumberValue]);

  const query = useCardNetworksApiLookup({
    apiHost,
    checkoutSessionToken,
    cardNumber: formattedCardNumberValue,
    experienceType,
    encryptionKey: merchantSettings?.encryptionKey,
    target,
    isFieldLengthValidForLookup,
    merchantCobrandedCardsEnabled: isCobrandedCardsEnabled,
    paymentType,
    version,
  });

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (query.isFetching) {
      timer = setTimeout(() => {
        if (query.isFetching) {
          setIsCardNetworksApiLookupTooLong(true);
          clearTimeout(timer);
        }
      }, 2000);
    } else {
      setIsCardNetworksApiLookupTooLong(false);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [query.isFetching]);

  useEffect(() => {
    if (
      query.status === "success" &&
      query.data &&
      isFieldLengthValidForLookup
    ) {
      setHadSuccessfulQuery(true);
      if (!possibleCardNetworksApi && !isCardNetworksApiLookupTooLong) {
        // is undefined, never run
        setIsCardNetworksApiLookupTooLong(false);
        setPossibleCardNetworksApi(query.data);
      } else if (
        possibleCardNetworksApi &&
        !isCardNetworksApiLookupTooLong &&
        !isArrayOfIntegersEqual(possibleCardNetworksApi, query.data)
      ) {
        setIsCardNetworksApiLookupTooLong(false);
        setPossibleCardNetworksApi(query.data);
      } else {
        // do nothing
      }
    }
  }, [query.status, query.data, isFieldLengthValidForLookup]);

  return {
    ...query,
    isCardNetworksApiLookupTooLong,
    possibleCardNetworksApi,
    setPossibleCardNetworksApi,
  };
};
